/**
 * Manager page: review and approve team members' annual KPI plans.
 * - Shows all direct reports with their plan status
 * - Manager can view/edit each employee's individual KPIs
 * - Manager can add/edit/delete individual KPIs before approving
 * - Approve or reject the plan with an optional comment
 */

import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import type {
  AssessmentPeriod,
  KPIAssignment,
  KPIPlan,
  TeamKPIPlanRow,
  CreateMyKPIInput,
  KPIPlanStatus,
} from '../types/penilaian.types'
import {
  KPI_PLAN_STATUS_LABELS,
  KPI_PLAN_STATUS_COLORS,
} from '../types/penilaian.types'

// ─── Types ────────────────────────────────────────────────────────────────────

interface AnnualKPIRow {
  name: string
  description?: string | null
  annual_target?: string | null
  kpi_type: 'company' | 'individual'
  department?: string | null
  q1?: KPIAssignment
  q2?: KPIAssignment
  q3?: KPIAssignment
  q4?: KPIAssignment
}

type YearPeriods = {
  Q1?: AssessmentPeriod
  Q2?: AssessmentPeriod
  Q3?: AssessmentPeriod
  Q4?: AssessmentPeriod
}

const QUARTER_FIELDS = [
  { q: 'Q1' as const, key: 'q1' as const, field: 'q1_target' as const },
  { q: 'Q2' as const, key: 'q2' as const, field: 'q2_target' as const },
  { q: 'Q3' as const, key: 'q3' as const, field: 'q3_target' as const },
  { q: 'Q4' as const, key: 'q4' as const, field: 'q4_target' as const },
]

function PlanStatusBadge({ status }: { status: KPIPlanStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${KPI_PLAN_STATUS_COLORS[status]}`}
    >
      {KPI_PLAN_STATUS_LABELS[status]}
    </span>
  )
}

// ─── KPI table for manager view ───────────────────────────────────────────────

interface KPIAnnualTableProps {
  rows: AnnualKPIRow[]
  yearPeriods: YearPeriods
  canEdit?: boolean
  onEdit?: (row: AnnualKPIRow) => void
  onDelete?: (row: AnnualKPIRow) => void
}

function KPIAnnualTable({
  rows,
  yearPeriods,
  canEdit = false,
  onEdit,
  onDelete,
}: KPIAnnualTableProps) {
  if (rows.length === 0) return null

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            <th className="text-left px-4 py-2.5 font-medium text-gray-600">
              Nama KPI
            </th>
            <th className="text-center px-3 py-2.5 font-medium text-gray-600 w-16">
              Bobot
            </th>
            <th className="text-left px-3 py-2.5 font-medium text-gray-600 w-36">
              Target Tahunan
            </th>
            {QUARTER_FIELDS.map(({ q }) => (
              <th
                key={q}
                className={`text-left px-3 py-2.5 font-medium w-32 ${
                  yearPeriods[q] ? 'text-gray-600' : 'text-gray-300'
                }`}
              >
                {q}
              </th>
            ))}
            {canEdit && <th className="w-20" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((row, i) => {
            const bobot =
              row.q1?.bobot ?? row.q2?.bobot ?? row.q3?.bobot ?? row.q4?.bobot ?? 0
            return (
              <tr key={`${row.name}-${i}`} className="hover:bg-gray-50/60">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{row.name}</div>
                  {row.description && (
                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {row.description}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-center font-semibold text-gray-800">
                  {bobot}%
                </td>
                <td className="px-3 py-3">
                  {row.annual_target ? (
                    <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                      {row.annual_target}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300 italic">—</span>
                  )}
                </td>
                {QUARTER_FIELDS.map(({ q, key }) => {
                  const period = yearPeriods[q]
                  const asgn = row[key]
                  if (!period) {
                    return (
                      <td key={q} className="px-3 py-3">
                        <span className="text-gray-200 text-xs">—</span>
                      </td>
                    )
                  }
                  const target = asgn?.target_value
                  return (
                    <td key={q} className="px-3 py-3">
                      <div className="text-xs text-gray-700 line-clamp-2">
                        {target ?? (
                          <span className="text-gray-300 italic">No target</span>
                        )}
                      </div>
                    </td>
                  )
                })}
                {canEdit && (
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onEdit?.(row)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDelete?.(row)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Add/Edit KPI modal ────────────────────────────────────────────────────────

interface EditKPIModalProps {
  row: AnnualKPIRow | null
  yearPeriods: YearPeriods
  employeeId: string
  year: number
  onClose: () => void
}

function EditKPIModal({
  row,
  yearPeriods,
  employeeId,
  year,
  onClose,
}: EditKPIModalProps) {
  const qc = useQueryClient()
  const isAdd = row === null
  const [form, setForm] = useState({
    name: row?.name ?? '',
    target_description: row?.description ?? '',
    annual_target: row?.annual_target ?? '',
    bobot: String(
      row?.q1?.bobot ?? row?.q2?.bobot ?? row?.q3?.bobot ?? row?.q4?.bobot ?? ''
    ),
    q1_target: row?.q1?.target_value ?? '',
    q2_target: row?.q2?.target_value ?? '',
    q3_target: row?.q3?.target_value ?? '',
    q4_target: row?.q4?.target_value ?? '',
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (isAdd) {
        const input: CreateMyKPIInput = {
          employee_id: employeeId,
          year,
          name: form.name,
          target_description: form.target_description || form.name,
          annual_target: form.annual_target || null,
          bobot: Number(form.bobot),
          q1_target: form.q1_target || null,
          q2_target: form.q2_target || null,
          q3_target: form.q3_target || null,
          q4_target: form.q4_target || null,
        }
        return penilaianApi.createEmployeeKPI(input)
      } else {
        const quarters = [
          { key: 'q1' as const, field: 'q1_target' as const },
          { key: 'q2' as const, field: 'q2_target' as const },
          { key: 'q3' as const, field: 'q3_target' as const },
          { key: 'q4' as const, field: 'q4_target' as const },
        ]
        for (const { key, field } of quarters) {
          const asgn = row![key]
          if (!asgn) continue
          await penilaianApi.updateKPIDefinition(asgn.kpi_id, {
            name: form.name,
            target_description: form.target_description || form.name,
            annual_target: form.annual_target || null,
          })
          await penilaianApi.updateKPIAssignment(asgn.id, {
            bobot: Number(form.bobot),
            target_value: form[field] || null,
          })
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-kpi-annual', employeeId, year] })
      toast.success(isAdd ? 'KPI berhasil ditambahkan' : 'KPI berhasil diperbarui')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menyimpan KPI'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="font-semibold text-gray-900 text-lg">
            {isAdd ? 'Tambah KPI untuk Karyawan' : 'Edit KPI'}
          </h3>
        </div>

        <form
          id="team-kpi-form"
          onSubmit={(e) => {
            e.preventDefault()
            mutation.mutate()
          }}
          className="overflow-y-auto flex-1 px-6 py-4 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nama KPI <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deskripsi <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.target_description}
              onChange={(e) =>
                setForm({ ...form, target_description: e.target.value })
              }
              rows={2}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bobot (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={form.bobot}
              onChange={(e) => setForm({ ...form, bobot: e.target.value })}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Tahunan
            </label>
            <input
              type="text"
              value={form.annual_target}
              onChange={(e) => setForm({ ...form, annual_target: e.target.value })}
              placeholder="Contoh: 95% customer satisfaction (opsional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target per Kuartal
            </label>
            <div className="space-y-2">
              {QUARTER_FIELDS.map(({ q, key, field }) => {
                const exists = !!yearPeriods[q]
                const hasAsgn = !isAdd ? !!row![key] : exists
                return (
                  <div
                    key={q}
                    className={`flex items-center gap-3 ${!exists ? 'opacity-40' : ''}`}
                  >
                    <span className="text-xs font-bold text-gray-500 w-7">{q}</span>
                    <input
                      type="text"
                      value={form[field]}
                      onChange={(e) =>
                        setForm({ ...form, [field]: e.target.value })
                      }
                      placeholder={!exists ? 'Periode belum dibuat' : `Target ${q}…`}
                      disabled={!exists || (!isAdd && !hasAsgn)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            type="submit"
            form="team-kpi-form"
            disabled={mutation.isPending}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menyimpan…' : isAdd ? 'Tambah KPI' : 'Simpan'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Delete modal ─────────────────────────────────────────────────────────────

interface DeleteKPIModalProps {
  row: AnnualKPIRow
  yearPeriods: YearPeriods
  employeeId: string
  onClose: () => void
}

function DeleteKPIModal({ row, employeeId, onClose }: DeleteKPIModalProps) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: async () => {
      const assignments = [row.q1, row.q2, row.q3, row.q4].filter(
        Boolean
      ) as KPIAssignment[]
      for (const asgn of assignments) {
        await penilaianApi.deleteKPIAssignment(asgn.id)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-kpi-annual', employeeId] })
      toast.success('KPI dihapus')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menghapus KPI'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-2">Hapus KPI?</h3>
        <p className="text-sm text-gray-500">
          <span className="font-medium text-gray-700">{row.name}</span> akan
          dihapus dari semua kuartal.
        </p>
        <div className="flex gap-3 mt-5">
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menghapus…' : 'Ya, Hapus'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Reject modal ─────────────────────────────────────────────────────────────

interface RejectModalProps {
  plan: KPIPlan
  onClose: () => void
  onRejected: (updated: KPIPlan) => void
}

function RejectModal({ plan, onClose, onRejected }: RejectModalProps) {
  const [comment, setComment] = useState('')
  const mutation = useMutation({
    mutationFn: () => penilaianApi.rejectKPIPlan(plan.id, comment),
    onSuccess: (updated) => {
      onRejected(updated)
      toast.success('Rencana KPI ditolak')
      onClose()
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menolak rencana KPI'),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-3">Tolak Rencana KPI</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Catatan penolakan <span className="text-red-500">*</span>
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder="Jelaskan alasan penolakan…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !comment.trim()}
            className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? 'Menolak…' : 'Tolak'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Employee KPI panel ───────────────────────────────────────────────────────

interface EmployeePanelProps {
  row: TeamKPIPlanRow
  yearPeriods: YearPeriods
  year: number
  isHRView: boolean
  onPlanUpdated: () => void
}

function EmployeePanel({ row, yearPeriods, year, isHRView, onPlanUpdated }: EmployeePanelProps) {
  const qc = useQueryClient()
  const [addModal, setAddModal] = useState(false)
  const [editingKPI, setEditingKPI] = useState<AnnualKPIRow | null>(null)
  const [deletingKPI, setDeletingKPI] = useState<AnnualKPIRow | null>(null)
  const [rejectModal, setRejectModal] = useState(false)

  const empId = row.employee.id
  const plan = row.plan

  const annualQuery = useQuery({
    queryKey: ['team-kpi-annual', empId, year],
    queryFn: () => penilaianApi.getEmployeeKPIsAnnual(empId, year),
  })

  const isLoading = annualQuery.isLoading

  const mergedRows = useMemo((): AnnualKPIRow[] => {
    const data = annualQuery.data ?? {}
    const map = new Map<string, AnnualKPIRow>()
    const QKEYS: [number, 'q1' | 'q2' | 'q3' | 'q4'][] = [
      [1, 'q1'], [2, 'q2'], [3, 'q3'], [4, 'q4'],
    ]
    for (const [q, qKey] of QKEYS) {
      for (const asgn of data[q] ?? []) {
        const name = asgn.kpi?.name ?? '(Tanpa Nama)'
        if (!map.has(name)) {
          map.set(name, {
            name,
            description: asgn.kpi?.target_description ?? null,
            annual_target: asgn.kpi?.annual_target ?? null,
            kpi_type: (asgn.kpi?.kpi_type as 'company' | 'individual') ?? 'individual',
            department: asgn.kpi?.department,
          })
        }
        map.get(name)![qKey] = asgn
      }
    }
    return Array.from(map.values())
  }, [annualQuery.data])

  const companyRows = mergedRows.filter((r) => r.kpi_type === 'company')
  const individualRows = mergedRows.filter((r) => r.kpi_type === 'individual')

  const isApproved = plan?.status === 'approved'
  const canEdit = !plan || ['draft', 'rejected_by_manager', 'rejected_by_hr'].includes(plan.status) || (isHRView && plan.status === 'pending_hr')

  const totalBobot = mergedRows.reduce(
    (s, r) =>
      s + (r.q1?.bobot ?? r.q2?.bobot ?? r.q3?.bobot ?? r.q4?.bobot ?? 0),
    0
  )

  const approveMutation = useMutation({
    mutationFn: () => penilaianApi.approveKPIPlan(plan!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-kpi-plans'] })
      onPlanUpdated()
      toast.success('Rencana KPI disetujui')
    },
    onError: (e: any) =>
      toast.error(e?.error?.message || 'Gagal menyetujui rencana KPI'),
  })

  return (
    <div className="space-y-4">
      {/* Employee header */}
      <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-900">
            {row.employee.display_name || row.employee.email}
          </h2>
          {row.employee.job_title && (
            <p className="text-xs text-gray-500 mt-0.5">
              {row.employee.job_title}
              {row.employee.department ? ` · ${row.employee.department}` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {plan ? (
            <PlanStatusBadge status={plan.status} />
          ) : (
            <span className="text-xs text-gray-400">Belum ada rencana KPI</span>
          )}
          {/* Approve / Reject — manager sees pending_manager; HR sees pending_hr */}
          {plan && ((isHRView && plan.status === 'pending_hr') || (!isHRView && plan.status === 'pending_manager')) && (
            <>
              <button
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
              >
                {approveMutation.isPending ? 'Menyetujui…' : 'Setujui'}
              </button>
              <button
                onClick={() => setRejectModal(true)}
                className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 text-sm font-medium"
              >
                Tolak
              </button>
            </>
          )}
        </div>
      </div>

      {/* Rejection comment */}
      {(plan?.status === 'rejected_by_manager' || plan?.status === 'rejected_by_hr') && plan.manager_comment && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          Catatan penolakan:{' '}
          <span className="font-medium">{plan.manager_comment}</span>
        </div>
      )}

      {/* Approved notice */}
      {isApproved && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium">
          Rencana KPI telah disetujui dan terkunci.
        </div>
      )}

      {/* Summary */}
      {!isLoading && (
        <div className="flex gap-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
          <div>
            <div className="text-xs text-blue-600">Total KPI</div>
            <div className="font-bold text-blue-700">{mergedRows.length}</div>
          </div>
          <div>
            <div className="text-xs text-blue-600">Total Bobot</div>
            <div
              className={`font-bold ${totalBobot === 100 ? 'text-green-600' : 'text-amber-600'}`}
            >
              {totalBobot}%
            </div>
          </div>
        </div>
      )}

      {/* Bobot warning */}
      {!isLoading && totalBobot !== 100 && individualRows.length > 0 && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-800">
              Total bobot harus 100% — saat ini {totalBobot}%
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {totalBobot < 100
                ? `Kurang ${100 - totalBobot}% — sesuaikan bobot KPI Individu karyawan ini hingga totalnya tepat 100%.`
                : `Lebih ${totalBobot - 100}% — kurangi bobot KPI Individu karyawan ini hingga totalnya tepat 100%.`}
            </p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-gray-400">Memuat KPI…</div>
      ) : (
        <>
          {/* Corporate KPIs */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900 text-sm">
                KPI Perusahaan
              </h3>
            </div>
            {companyRows.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-400">
                Belum ada KPI Perusahaan
              </div>
            ) : (
              <KPIAnnualTable
                rows={companyRows}
                yearPeriods={yearPeriods}
                canEdit={false}
              />
            )}
          </div>

          {/* Individual KPIs */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">
                KPI Individu
              </h3>
              {canEdit && (
                <button
                  onClick={() => setAddModal(true)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                >
                  + Tambah
                </button>
              )}
            </div>
            {individualRows.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-400">
                Belum ada KPI Individu
              </div>
            ) : (
              <KPIAnnualTable
                rows={individualRows}
                yearPeriods={yearPeriods}
                canEdit={canEdit}
                onEdit={setEditingKPI}
                onDelete={setDeletingKPI}
              />
            )}
          </div>
        </>
      )}

      {addModal && (
        <EditKPIModal
          row={null}
          yearPeriods={yearPeriods}
          employeeId={empId}
          year={year}
          onClose={() => setAddModal(false)}
        />
      )}
      {editingKPI && (
        <EditKPIModal
          row={editingKPI}
          yearPeriods={yearPeriods}
          employeeId={empId}
          year={year}
          onClose={() => setEditingKPI(null)}
        />
      )}
      {deletingKPI && (
        <DeleteKPIModal
          row={deletingKPI}
          yearPeriods={yearPeriods}
          employeeId={empId}
          onClose={() => setDeletingKPI(null)}
        />
      )}
      {rejectModal && plan && (
        <RejectModal
          plan={plan}
          onClose={() => setRejectModal(false)}
          onRejected={() => {
            qc.invalidateQueries({ queryKey: ['team-kpi-plans'] })
            onPlanUpdated()
          }}
        />
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TeamKPIPage({ isHRView = false }: { isHRView?: boolean }) {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedEmpId, setSelectedEmpId] = useState<string | null>(null)

  // ── Periods ───────────────────────────────────────────────────────────────

  const { data: periodsData } = useQuery({
    queryKey: ['penilaian-periods'],
    queryFn: () => penilaianApi.getPeriods(),
  })

  const periods = periodsData?.data ?? []

  const yearPeriods: YearPeriods = useMemo(
    () => ({
      Q1: periods.find((p) => p.year === selectedYear && p.quarter === 1),
      Q2: periods.find((p) => p.year === selectedYear && p.quarter === 2),
      Q3: periods.find((p) => p.year === selectedYear && p.quarter === 3),
      Q4: periods.find((p) => p.year === selectedYear && p.quarter === 4),
    }),
    [periods, selectedYear]
  )

  const availableYears = useMemo(() => {
    const ys = new Set(periods.map((p) => p.year))
    ys.add(currentYear)
    return Array.from(ys).sort((a, b) => b - a)
  }, [periods, currentYear])

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableYears])

  // ── Team plans ────────────────────────────────────────────────────────────

  const { data: teamPlans, isLoading, refetch } = useQuery<TeamKPIPlanRow[]>({
    queryKey: ['team-kpi-plans', selectedYear, isHRView],
    queryFn: () => penilaianApi.getTeamKPIPlans(selectedYear, isHRView),
  })

  const rows = teamPlans ?? []
  const selectedRow = rows.find((r) => r.employee.id === selectedEmpId) ?? null

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isHRView ? 'Rencana KPI Karyawan' : 'KPI Tim'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isHRView
              ? 'Tinjau dan setujui rencana KPI semua karyawan aktif'
              : 'Tinjau dan setujui rencana KPI anggota tim Anda'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Tahun</span>
          <select
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(Number(e.target.value))
              setSelectedEmpId(null)
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex gap-4" style={{ minHeight: '70vh' }}>
        {/* Team member list */}
        <div className="w-64 flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {isHRView ? 'Semua Karyawan' : 'Anggota Tim'}
            </p>
          </div>
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="text-center py-8 text-sm text-gray-400">
                Memuat…
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400 px-4">
                {isHRView ? 'Tidak ada karyawan aktif' : 'Tidak ada bawahan langsung'}
              </div>
            ) : (
              rows.map((r) => (
                <button
                  key={r.employee.id}
                  onClick={() => setSelectedEmpId(r.employee.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${
                    selectedEmpId === r.employee.id
                      ? 'bg-blue-50 border-l-2 border-l-blue-500'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 leading-tight truncate">
                    {r.employee.display_name || r.employee.email}
                  </div>
                  <div className="mt-1">
                    {r.plan ? (
                      <PlanStatusBadge status={r.plan.status} />
                    ) : (
                      <span className="text-xs text-gray-400">Belum ada rencana</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-0">
          {!selectedRow ? (
            <div className="h-full flex items-center justify-center text-gray-400 bg-white border border-dashed border-gray-300 rounded-xl">
              <div className="text-center">
                <svg
                  className="w-12 h-12 mx-auto mb-3 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <p className="text-sm">Pilih anggota tim di sebelah kiri</p>
              </div>
            </div>
          ) : (
            <EmployeePanel
              key={selectedRow.employee.id}
              row={selectedRow}
              yearPeriods={yearPeriods}
              year={selectedYear}
              isHRView={isHRView}
              onPlanUpdated={() => refetch()}
            />
          )}
        </div>
      </div>
    </div>
  )
}
